import axios from "axios";
import { saveAs } from "file-saver";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  convertInchesToTwip,
  BorderStyle,
  Table,
  TableRow,
  TableCell,
  WidthType,
  VerticalAlign,
  ImageRun,
  Footer,
} from "docx";

// Label width for consistent key-value alignment (in twips)
// Reduced from 2 inches to 1.5 inches to give more space to content
const LABEL_WIDTH = convertInchesToTwip(1.5);

// Image URLs (relative to public folder)
const LOGO_URL = "/static/img/clearwater-logo-full.png";
const SIGNATURE_URL = "/static/img/gm-signature-whitney-ingram.jpg";

// Conversion constant: 1 acre-foot = 325,851 gallons
const GALLONS_PER_ACRE_FOOT = 325851;

// Cache for image data
let cachedLogoData = null;
let cachedSignatureData = null;

/**
 * Fetch an image as ArrayBuffer (with caching)
 */
async function fetchImageData(url, cache, setCacheCallback) {
  if (cache) {
    return cache;
  }
  try {
    const response = await fetch(url);
    if (!response.ok) {
      console.warn(`Failed to fetch image from ${url}:`, response.status);
      return null;
    }
    const data = await response.arrayBuffer();
    setCacheCallback(data);
    return data;
  } catch (err) {
    console.warn(`Error fetching image from ${url}:`, err);
    return null;
  }
}

async function fetchLogoData() {
  return fetchImageData(LOGO_URL, cachedLogoData, (data) => {
    cachedLogoData = data;
  });
}

async function fetchSignatureData() {
  return fetchImageData(SIGNATURE_URL, cachedSignatureData, (data) => {
    cachedSignatureData = data;
  });
}

/**
 * Get ordinal suffix for a number (1st, 2nd, 3rd, etc.)
 */
function getOrdinalSuffix(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

/**
 * Create logo cell content - either ImageRun or placeholder text
 * Logo original dimensions: 585x189 (ratio 3.095:1)
 * Using width: 180, height: 58 to maintain correct aspect ratio
 */
function createLogoContent(logoData) {
  if (logoData) {
    return new Paragraph({
      children: [
        new ImageRun({
          data: logoData,
          transformation: {
            width: 180,
            height: 58,
          },
          type: "png",
        }),
      ],
    });
  }
  return new Paragraph({
    children: [
      new TextRun({
        text: "[LOGO]",
        italics: true,
        size: 20,
        color: "999999",
      }),
    ],
  });
}

/**
 * Create the header table with logo and organization info
 */
function createHeaderTable(logoData) {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.NONE },
      bottom: { style: BorderStyle.NONE },
      left: { style: BorderStyle.NONE },
      right: { style: BorderStyle.NONE },
      insideHorizontal: { style: BorderStyle.NONE },
      insideVertical: { style: BorderStyle.NONE },
    },
    rows: [
      new TableRow({
        children: [
          // Logo cell (left)
          new TableCell({
            width: { size: 30, type: WidthType.PERCENTAGE },
            verticalAlign: VerticalAlign.CENTER,
            borders: {
              top: { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
            },
            children: [createLogoContent(logoData)],
          }),
          // Text cell (right)
          new TableCell({
            width: { size: 70, type: WidthType.PERCENTAGE },
            verticalAlign: VerticalAlign.CENTER,
            borders: {
              top: { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
            },
            children: [
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                spacing: { after: 0 },
                children: [
                  new TextRun({
                    text: "Clearwater Underground Water",
                    italics: true,
                    size: 28,
                  }),
                ],
              }),
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                spacing: { after: 0 },
                children: [
                  new TextRun({
                    text: "Conservation District",
                    italics: true,
                    size: 28,
                  }),
                ],
              }),
              new Paragraph({
                alignment: AlignmentType.RIGHT,
                spacing: { after: 0 },
                children: [
                  new TextRun({
                    text: "700 Kennedy Court, Belton, Texas 76513",
                    italics: true,
                    size: 20,
                  }),
                ],
              }),
            ],
          }),
        ],
      }),
    ],
  });
}

// Helper to create a key-value row as a borderless table
function createKeyValueRow(label, value, options = {}) {
  const { labelBold = true, valueBold = false, valueItalic = false } = options;
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      top: { style: BorderStyle.NONE },
      bottom: { style: BorderStyle.NONE },
      left: { style: BorderStyle.NONE },
      right: { style: BorderStyle.NONE },
      insideHorizontal: { style: BorderStyle.NONE },
      insideVertical: { style: BorderStyle.NONE },
    },
    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: { size: LABEL_WIDTH, type: WidthType.DXA },
            borders: {
              top: { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
            },
            children: [
              new Paragraph({
                children: [
                  new TextRun({ text: label, bold: labelBold, size: 22 }),
                ],
              }),
            ],
          }),
          new TableCell({
            borders: {
              top: { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
            },
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: value || "—",
                    bold: valueBold,
                    italics: valueItalic,
                    size: 22,
                  }),
                ],
              }),
            ],
          }),
        ],
      }),
    ],
  });
}

/**
 * Create key-value table with 3-column grid content for well numbers
 * Label on left (first row only), then 3 equal columns for values
 * Items wrap across rows: item1→col1, item2→col2, item3→col3, item4→col1 (row2), etc.
 */
function createWellNumberRow(label, wellNumbers) {
  const noBorders = {
    top: { style: BorderStyle.NONE },
    bottom: { style: BorderStyle.NONE },
    left: { style: BorderStyle.NONE },
    right: { style: BorderStyle.NONE },
  };

  const wells = wellNumbers || [];

  // Calculate content column width - page is ~7 inches with 0.75-inch margins
  // Label is 1.5 inches, so content is ~5.5 inches, each column ~1.83 inches
  const CONTENT_COL_WIDTH = convertInchesToTwip(1.83);

  // Split wells into rows of 3
  const rows = [];
  for (let i = 0; i < wells.length; i += 3) {
    rows.push(wells.slice(i, i + 3));
  }

  // If no wells, create one row with dash
  if (rows.length === 0) {
    rows.push(["—"]);
  }

  // Build table rows
  const tableRows = rows.map((rowItems, rowIndex) => {
    return new TableRow({
      children: [
        // Label cell (only show label on first row)
        new TableCell({
          width: { size: LABEL_WIDTH, type: WidthType.DXA },
          borders: noBorders,
          children: [
            new Paragraph({
              children:
                rowIndex === 0
                  ? [new TextRun({ text: label, bold: true, size: 22 })]
                  : [],
            }),
          ],
        }),
        // 3 content columns
        new TableCell({
          width: { size: CONTENT_COL_WIDTH, type: WidthType.DXA },
          borders: noBorders,
          children: [
            new Paragraph({
              children: [new TextRun({ text: rowItems[0] || "", size: 22 })],
            }),
          ],
        }),
        new TableCell({
          width: { size: CONTENT_COL_WIDTH, type: WidthType.DXA },
          borders: noBorders,
          children: [
            new Paragraph({
              children: [new TextRun({ text: rowItems[1] || "", size: 22 })],
            }),
          ],
        }),
        new TableCell({
          width: { size: CONTENT_COL_WIDTH, type: WidthType.DXA },
          borders: noBorders,
          children: [
            new Paragraph({
              children: [new TextRun({ text: rowItems[2] || "", size: 22 })],
            }),
          ],
        }),
      ],
    });
  });

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    borders: {
      ...noBorders,
      insideHorizontal: { style: BorderStyle.NONE },
      insideVertical: { style: BorderStyle.NONE },
    },
    rows: tableRows,
  });
}

// Permit conditions for page 2 based on permit type
const PERMIT_CONDITIONS_INTRO =
  "All permits are granted subject to the Rules, regulations, orders, special provisions, and other requirements of the Board, and the law of the State of Texas. In addition, each permit issued shall be subject to the following conditions and requirements:";

// Type 2: Historic and Existing Use Permits
const PERMIT_CONDITIONS_TYPE_2 = [
  "A. The permit is granted in accordance with the provisions of Chapter 36, Texas Water Code, and the Rules, regulations and orders of the District as may be in effect from time to time, and acceptance of the permit constitutes an acknowledgement and agreement that the permittee will comply with all the terms, provisions, conditions, requirements, limitations, and restrictions embodied in the permit and with the Rules, regulations, and orders of the District.",
  "B. The permit confers no vested rights in the holder and the permit is transferable only upon compliance with the District's rules governing transfers. Written notice must be given to the District by the permittee prior to any sale or lease of the well covered by the permit. The permit may be revoked or suspended for failure to comply with its terms, which may be modified or amended pursuant to the requirements of State law and any applicable Rules, regulations and orders of the District.",
  "C. A permit may be subject to amendment by the District of the amount of water authorized for pumpage based upon a review of the District's groundwater availability model and a determination by the District that an amendment is necessary after considering adequate water levels in water supply wells and degradation of water quality that could result from low water levels and/or low spring discharge.",
  "D. The operation of the well for the authorized use shall be conducted in such a manner as to avoid waste, pollution, or harm to the aquifers.",
  "E. The permittee, unless qualifying for a metering and reporting exception, shall 1) keep accurate records and meter readings, on a monthly basis, of the amount of groundwater withdrawn, the purpose of the withdrawal, and, for any transporting of water outside the District, the amount of water transported and the identity and location of the recipients; 2) report total withdrawals to the District monthly; and 3) make all records available for inspection at the permittee's principal place of business by District representatives. All permittees shall provide immediate written notice to the District in the event a withdrawal or transportation of water exceeds the quantity authorized by the permit or rules. Unless the permittee can present evidence that the pumpage or transport which exceeded the permitted amount is due to an isolated incident that is not likely to be repeated and/or would not result in continued higher demands, the permittee must immediately submit an application to increase the permitted pumpage or transport volume based on the amount of pumpage or transport which exceeded the permitted amount projected for the remainder of the year.",
  "F. The well site and transport facilities shall be accessible to District representatives for inspection during normal business hours and during emergencies. The permittee agrees to cooperate fully in any reasonable inspection of the well site or transport facilities and related monitoring or sampling by District representatives. The well owner shall provide a twenty-four (24) hour emergency contact to the District.",
  "G. The application pursuant to which this permit has been issued is incorporated herein, and this permit is granted on the basis of and contingent upon the accuracy of the information supplied in that application and in any amendment thereof. A finding that false information has been supplied shall be grounds for immediate revocation of a permit. In the event of conflict between the provisions of the permit and the contents of the application, the provisions of the permit shall prevail.",
  "H. Monitoring of groundwater pumpage is to be accomplished in the manner specified in the District's metering policy and any modifications thereto.",
  "I. Violation of the permit's terms, conditions, requirements, or special provisions, including pumping amounts in excess of authorized withdrawal or transporting amounts outside of the District in excess of the amount authorized for transport, shall be punishable by civil penalties as provided by State law and the District's Rules.",
  "J. If special provisions are inconsistent with other provisions or regulations of the District, the special provisions shall prevail.",
  "K. Permittee will notify the District upon filing an application with TCEQ to obtain or modify CCN to provide water or wastewater services in a service area that lies wholly or partly within the District or for which water shall be supplied from a well located inside the District.",
];

// Type 1 or 3: Drilling & Operating Permits
const PERMIT_CONDITIONS_TYPE_1_3 = [
  "A. The permit is granted in accordance with the provisions of Chapter 36, Texas Water Code, and the Rules, regulations and orders of the District as may be in effect from time to time, and acceptance of the permit constitutes an acknowledgement and agreement that the permittee will comply with all the terms, provisions, conditions, requirements, limitations, and restrictions embodied in the permit and with the Rules, regulations, and orders of the District.",
  "B. The permit confers no vested rights in the holder and the permit is transferable only upon compliance with the District's rules governing transfers. Written notice must be given to the District by the permittee prior to any sale or lease of the well covered by the permit. The permit may be revoked or suspended for failure to comply with its terms, which may be modified or amended pursuant to the requirements of State law and any applicable Rules, regulations and orders of the District.",
  "C. The well shall be located and completed as required in District rules and 16 Texas Administrative Code, Chapter 76.1000. The well shall observe spacing requirements specifically stated in the District rules.",
  "D. A permit shall be subject to amendment by the District of the amount of water authorized for pumpage based upon a review of the District's groundwater availability model and a determination by the District that an amendment is necessary after considering adequate water levels in water supply wells and degradation of water quality that could result from low water levels and/or low spring discharge.",
  "E. The drilling and operation of the well for the authorized use shall be conducted in such a manner as to avoid waste, pollution, or harm to the aquifers.",
  "F. The permittee, unless qualifying for a metering and reporting exception, shall 1) keep accurate records and meter readings, on a monthly basis, of the amount of groundwater withdrawn, the purpose of the withdrawal, and, for any transporting of water outside the District, the amount of water transported and the identity and location of the recipients; 2) report total withdrawals to the District monthly; and 3) make all records available for inspection at the permittee's principal place of business by District representatives. All permittees shall provide immediate written notice to the District in the event a withdrawal or transportation of water exceeds the quantity authorized by the permit or rules. Unless the permittee can present evidence that the pumpage or transport which exceeded the permitted amount is due to an isolated incident that is not likely to be repeated and/or would not result in continued higher demands, the permittee must immediately submit an application to increase the permitted pumpage or transport volume based on the amount of pumpage or transport which exceeded the permitted amount projected for the remainder of the year.",
  "G. The well site and transport facilities shall be accessible to District representatives for inspection during normal business hours and during emergencies. The permittee agrees to cooperate fully in any reasonable inspection of the well site or transport facilities and related monitoring or sampling by District representatives. The well owner shall provide a twenty-four (24) hour emergency contact to the District.",
  "H. The application pursuant to which this permit has been issued is incorporated therein, and this permit is granted on the basis of and contingent upon the accuracy of the information supplied in that application and in any amendment thereof. A finding that false information has been supplied shall be grounds for immediate revocation of a permit. In the event of conflict between the provisions of the permit and the contents of the application, the provisions of the permit shall prevail.",
  "I. Driller's logs must be submitted within sixty (60) days of the drilling of a well. Monitoring of groundwater pumpage is to be accomplished in the manner specified in the District's metering policy and any modifications thereto.",
  "J. Violation of the permit's terms, conditions, requirements, or special provisions, including pumping amounts in excess of authorized withdrawal or transporting amounts outside of the District in excess of the amount authorized for transport, shall be punishable by civil penalties as provided by State law and the District's Rules.",
  "K. If special provisions are inconsistent with other provisions or regulations of the District, the special provisions shall prevail.",
  "L. Permittee will notify the District upon filing an application with TCEQ to obtain or modify CCN to provide water or wastewater services in a service area that lies wholly or partly within the District or for which water shall be supplied from a well located inside the District.",
];

/**
 * Get permit conditions based on permit_type_ndx
 */
function getPermitConditions(permitTypeNdx) {
  if (permitTypeNdx === 2) {
    return {
      subtitle: "Applicable to Historic and Existing Use Permits",
      conditions: PERMIT_CONDITIONS_TYPE_2,
    };
  }
  // Default to type 1/3 (Drilling & Operating)
  return {
    subtitle: "Applicable to Drilling & Operating Permits",
    conditions: PERMIT_CONDITIONS_TYPE_1_3,
  };
}

const API_ENDPOINT = process.env.REACT_APP_ENDPOINT;

/**
 * Fetch single permit export data
 */
export async function fetchPermitExportData(permitNdx, token) {
  const response = await axios.get(
    `${API_ENDPOINT}/api/permit-export/${permitNdx}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return response.data;
}

/**
 * Create permit DOCX document matching real CUWCD permit format
 * @param {Object} permit - The permit data
 * @param {Object} options - Options including logoData, signatureData, issuedDate
 */
export function createPermitDocument(permit, options = {}) {
  const {
    logoData = null,
    signatureData = null,
    issuedDate = new Date(),
  } = options;

  // Format contact name
  const contactName = [permit.firstname, permit.lastname]
    .filter(Boolean)
    .join(" ");

  // Build owner/permittee lines with new logic:
  // - If org exists: Line 1 = org, Line 2 = "c/o name (phone)" or just phone
  // - If no org: Line 1 = "name (phone)" without c/o prefix
  // - If nothing: show dash
  const ownerLines = [];
  if (permit.organization) {
    ownerLines.push(permit.organization);
    if (contactName) {
      ownerLines.push(
        `c/o ${contactName}${permit.phone_1 ? ` (${permit.phone_1})` : ""}`
      );
    } else if (permit.phone_1) {
      ownerLines.push(permit.phone_1);
    }
  } else if (contactName) {
    ownerLines.push(
      `${contactName}${permit.phone_1 ? ` (${permit.phone_1})` : ""}`
    );
  } else {
    ownerLines.push("—");
  }

  // Format mailing address parts
  const addressParts = [];
  if (permit.address) addressParts.push(permit.address);
  const cityStateZip = [permit.city, permit.state, permit.zipcode]
    .filter(Boolean)
    .join(", ");
  if (cityStateZip) addressParts.push(cityStateZip);

  // Well numbers as arrays for 3-column layout
  const wellNumbers = permit.associated_wells || [];
  const districtWellNosArray = wellNumbers
    .map((w) => w.cuwcd_well_number)
    .filter(Boolean);
  const stateWellNosArray = wellNumbers
    .map((w) => w.state_well_number)
    .filter(Boolean);

  // Aggregate system info
  const isAggregate = permit.agg_system_ndx && permit.well_count > 1;
  const aggregateDesc = isAggregate
    ? `${permit.well_count} Well Aggregate System`
    : null;

  // Withdrawal amounts - DB value is in acre-feet, convert to gallons
  // Use != null to accept 0 as a valid value
  const acft =
    permit.permitted_value != null
      ? Number(permit.permitted_value).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "—";
  const gallons =
    permit.permitted_value != null
      ? Math.round(
          permit.permitted_value * GALLONS_PER_ACRE_FOOT
        ).toLocaleString()
      : "—";

  // Permit type title
  const permitTypeTitle = permit.permit_type_desc
    ? `${permit.permit_type_desc} Permit`
    : "Operating Permit";

  const currentYear = new Date().getFullYear();

  // Format issued date
  const issuedDay = issuedDate.getDate();
  const issuedMonth = issuedDate.toLocaleString("en-US", { month: "long" });
  const issuedYear = issuedDate.getFullYear();

  // Get permit conditions based on type
  const { subtitle: conditionsSubtitle, conditions: permitConditions } =
    getPermitConditions(permit.permit_type_ndx);

  return new Document({
    styles: {
      default: {
        document: {
          run: { font: "Times New Roman", size: 22 },
          paragraph: { spacing: { line: 276 } },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(0.6),
              bottom: convertInchesToTwip(0.6),
              left: convertInchesToTwip(0.75),
              right: convertInchesToTwip(0.75),
            },
            borders: {
              pageBorderTop: {
                style: BorderStyle.SINGLE,
                size: 12,
                color: "000000",
                space: 10,
              },
              pageBorderBottom: {
                style: BorderStyle.SINGLE,
                size: 12,
                color: "000000",
                space: 10,
              },
              pageBorderLeft: {
                style: BorderStyle.SINGLE,
                size: 12,
                color: "000000",
                space: 10,
              },
              pageBorderRight: {
                style: BorderStyle.SINGLE,
                size: 12,
                color: "000000",
                space: 10,
              },
              pageBorders: {
                offsetFrom: "page",
              },
            },
          },
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    text: `Permit No: ${permit.permit_number || "—"}`,
                    size: 18,
                    color: "666666",
                  }),
                ],
              }),
            ],
          }),
        },
        children: [
          // === HEADER (Logo left, Text right) ===
          createHeaderTable(logoData),

          // Horizontal line
          new Paragraph({
            border: {
              bottom: { style: BorderStyle.SINGLE, size: 12, color: "000000" },
            },
            spacing: { before: 200, after: 200 },
          }),

          // === PERMIT TYPE TITLE ===
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { before: 60, after: 120 },
            children: [
              new TextRun({
                text: permitTypeTitle,
                bold: true,
                size: 44, // 22pt
              }),
            ],
          }),

          // === KEY-VALUE PAIRS ===
          // Permit Number (with aggregate description as second line if applicable)
          createKeyValueRow("Permit No:", permit.permit_number || "—", {
            valueBold: true,
          }),
          ...(aggregateDesc
            ? [
                new Table({
                  width: { size: 100, type: WidthType.PERCENTAGE },
                  borders: {
                    top: { style: BorderStyle.NONE },
                    bottom: { style: BorderStyle.NONE },
                    left: { style: BorderStyle.NONE },
                    right: { style: BorderStyle.NONE },
                    insideHorizontal: { style: BorderStyle.NONE },
                    insideVertical: { style: BorderStyle.NONE },
                  },
                  rows: [
                    new TableRow({
                      children: [
                        new TableCell({
                          width: { size: LABEL_WIDTH, type: WidthType.DXA },
                          borders: {
                            top: { style: BorderStyle.NONE },
                            bottom: { style: BorderStyle.NONE },
                            left: { style: BorderStyle.NONE },
                            right: { style: BorderStyle.NONE },
                          },
                          children: [new Paragraph({})],
                        }),
                        new TableCell({
                          borders: {
                            top: { style: BorderStyle.NONE },
                            bottom: { style: BorderStyle.NONE },
                            left: { style: BorderStyle.NONE },
                            right: { style: BorderStyle.NONE },
                          },
                          children: [
                            new Paragraph({
                              children: [
                                new TextRun({
                                  text: `${aggregateDesc}${
                                    permit.agg_system_name
                                      ? ` (${permit.agg_system_name})`
                                      : ""
                                  }`,
                                  italics: true,
                                  size: 20,
                                }),
                              ],
                            }),
                          ],
                        }),
                      ],
                    }),
                  ],
                }),
              ]
            : []),
          new Paragraph({ spacing: { after: 50 } }),

          // Owner/Permittee
          createKeyValueRow("Owner/Permittee:", ownerLines[0]),
          ...(ownerLines.length > 1
            ? ownerLines.slice(1).map(
                (line) =>
                  new Table({
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    borders: {
                      top: { style: BorderStyle.NONE },
                      bottom: { style: BorderStyle.NONE },
                      left: { style: BorderStyle.NONE },
                      right: { style: BorderStyle.NONE },
                      insideHorizontal: { style: BorderStyle.NONE },
                      insideVertical: { style: BorderStyle.NONE },
                    },
                    rows: [
                      new TableRow({
                        children: [
                          new TableCell({
                            width: { size: LABEL_WIDTH, type: WidthType.DXA },
                            borders: {
                              top: { style: BorderStyle.NONE },
                              bottom: { style: BorderStyle.NONE },
                              left: { style: BorderStyle.NONE },
                              right: { style: BorderStyle.NONE },
                            },
                            children: [new Paragraph({})],
                          }),
                          new TableCell({
                            borders: {
                              top: { style: BorderStyle.NONE },
                              bottom: { style: BorderStyle.NONE },
                              left: { style: BorderStyle.NONE },
                              right: { style: BorderStyle.NONE },
                            },
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({ text: line, size: 22 }),
                                ],
                              }),
                            ],
                          }),
                        ],
                      }),
                    ],
                  })
              )
            : []),
          new Paragraph({ spacing: { after: 50 } }),

          // Mailing Address
          createKeyValueRow("Mailing Address:", addressParts[0] || "—"),
          ...(addressParts.length > 1
            ? addressParts.slice(1).map(
                (line) =>
                  new Table({
                    width: { size: 100, type: WidthType.PERCENTAGE },
                    borders: {
                      top: { style: BorderStyle.NONE },
                      bottom: { style: BorderStyle.NONE },
                      left: { style: BorderStyle.NONE },
                      right: { style: BorderStyle.NONE },
                      insideHorizontal: { style: BorderStyle.NONE },
                      insideVertical: { style: BorderStyle.NONE },
                    },
                    rows: [
                      new TableRow({
                        children: [
                          new TableCell({
                            width: { size: LABEL_WIDTH, type: WidthType.DXA },
                            borders: {
                              top: { style: BorderStyle.NONE },
                              bottom: { style: BorderStyle.NONE },
                              left: { style: BorderStyle.NONE },
                              right: { style: BorderStyle.NONE },
                            },
                            children: [new Paragraph({})],
                          }),
                          new TableCell({
                            borders: {
                              top: { style: BorderStyle.NONE },
                              bottom: { style: BorderStyle.NONE },
                              left: { style: BorderStyle.NONE },
                              right: { style: BorderStyle.NONE },
                            },
                            children: [
                              new Paragraph({
                                children: [
                                  new TextRun({ text: line, size: 22 }),
                                ],
                              }),
                            ],
                          }),
                        ],
                      }),
                    ],
                  })
              )
            : []),
          new Paragraph({ spacing: { after: 50 } }),

          // Well Numbers - standard key-value with 3-column content
          createWellNumberRow("District Well No:", districtWellNosArray),
          new Paragraph({ spacing: { after: 40 } }),
          createWellNumberRow("State Well No:", stateWellNosArray),
          new Paragraph({ spacing: { after: 50 } }),

          // Terms
          createKeyValueRow(
            "Terms:",
            `Permit expires December 31, ${permit.permit_year || currentYear}.`
          ),
          new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
              top: { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
              insideHorizontal: { style: BorderStyle.NONE },
              insideVertical: { style: BorderStyle.NONE },
            },
            rows: [
              new TableRow({
                children: [
                  new TableCell({
                    width: { size: LABEL_WIDTH, type: WidthType.DXA },
                    borders: {
                      top: { style: BorderStyle.NONE },
                      bottom: { style: BorderStyle.NONE },
                      left: { style: BorderStyle.NONE },
                      right: { style: BorderStyle.NONE },
                    },
                    children: [new Paragraph({})],
                  }),
                  new TableCell({
                    borders: {
                      top: { style: BorderStyle.NONE },
                      bottom: { style: BorderStyle.NONE },
                      left: { style: BorderStyle.NONE },
                      right: { style: BorderStyle.NONE },
                    },
                    children: [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: "Failure to abide by District rules and permit conditions will subject this permit to revocation.",
                            size: 22,
                          }),
                        ],
                      }),
                    ],
                  }),
                ],
              }),
              new TableRow({
                children: [
                  new TableCell({
                    width: { size: LABEL_WIDTH, type: WidthType.DXA },
                    borders: {
                      top: { style: BorderStyle.NONE },
                      bottom: { style: BorderStyle.NONE },
                      left: { style: BorderStyle.NONE },
                      right: { style: BorderStyle.NONE },
                    },
                    children: [new Paragraph({})],
                  }),
                  new TableCell({
                    borders: {
                      top: { style: BorderStyle.NONE },
                      bottom: { style: BorderStyle.NONE },
                      left: { style: BorderStyle.NONE },
                      right: { style: BorderStyle.NONE },
                    },
                    children: [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: "See page 2 for permit conditions and requirements.",
                            size: 22,
                          }),
                        ],
                      }),
                    ],
                  }),
                ],
              }),
            ],
          }),
          new Paragraph({ spacing: { after: 60 } }),

          // === PERMITTED WITHDRAWAL ===
          new Paragraph({
            spacing: { before: 100, after: 60 },
            border: {
              top: { style: BorderStyle.SINGLE, size: 6, color: "000000" },
              bottom: { style: BorderStyle.SINGLE, size: 6, color: "000000" },
            },
            shading: { fill: "F0F0F0" },
            children: [
              new TextRun({
                text: "Maximum Annual Permitted Withdrawal:",
                bold: true,
                size: 22,
              }),
            ],
          }),
          new Paragraph({
            indent: { left: convertInchesToTwip(0.5) },
            spacing: { after: 100 },
            children: [
              new TextRun({
                text: `${gallons} gallons or ${acft} ac-ft`,
                bold: true,
                size: 24,
              }),
            ],
          }),

          // === PERMIT DETAILS ===
          createKeyValueRow("Management Zone:", "Southwest Management Zone"),
          new Paragraph({ spacing: { after: 50 } }),
          createKeyValueRow("Aquifer:", permit.aquifer_name || "—"),
          new Paragraph({ spacing: { after: 50 } }),
          createKeyValueRow("Use:", permit.permitted_use || "—"),
          new Paragraph({ spacing: { after: 50 } }),

          // === SPECIAL PROVISIONS ===
          createKeyValueRow(
            "Special Provisions:",
            permit.special_provisions ||
              "Standard permit conditions apply. See page 2 for permit conditions and requirements."
          ),

          // === ISSUANCE LINE ===
          new Paragraph({
            spacing: { before: 200, after: 100 },
            children: [
              new TextRun({
                text: "This permit is hereby issued this ",
                size: 22,
              }),
              new TextRun({
                text: `${issuedDay}${getOrdinalSuffix(issuedDay)}`,
                size: 22,
              }),
              new TextRun({
                text: " day of ",
                size: 22,
              }),
              new TextRun({
                text: `${issuedMonth} ${issuedYear}`,
                size: 22,
              }),
              new TextRun({
                text: ".",
                size: 22,
              }),
            ],
          }),

          // === SIGNATURE ===
          // Table to keep "By:" inline with signature and underline only under signature
          new Table({
            width: { size: convertInchesToTwip(3), type: WidthType.DXA },
            borders: {
              top: { style: BorderStyle.NONE },
              bottom: { style: BorderStyle.NONE },
              left: { style: BorderStyle.NONE },
              right: { style: BorderStyle.NONE },
              insideHorizontal: { style: BorderStyle.NONE },
              insideVertical: { style: BorderStyle.NONE },
            },
            rows: [
              new TableRow({
                children: [
                  // "By:" cell
                  new TableCell({
                    width: {
                      size: convertInchesToTwip(0.4),
                      type: WidthType.DXA,
                    },
                    verticalAlign: VerticalAlign.BOTTOM,
                    borders: {
                      top: { style: BorderStyle.NONE },
                      bottom: { style: BorderStyle.NONE },
                      left: { style: BorderStyle.NONE },
                      right: { style: BorderStyle.NONE },
                    },
                    children: [
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: "By:",
                            size: 22,
                          }),
                        ],
                      }),
                    ],
                  }),
                  // Signature image cell with bottom border
                  new TableCell({
                    width: {
                      size: convertInchesToTwip(2),
                      type: WidthType.DXA,
                    },
                    verticalAlign: VerticalAlign.BOTTOM,
                    borders: {
                      top: { style: BorderStyle.NONE },
                      bottom: {
                        style: BorderStyle.SINGLE,
                        size: 6,
                        color: "000000",
                      },
                      left: { style: BorderStyle.NONE },
                      right: { style: BorderStyle.NONE },
                    },
                    children: [
                      new Paragraph({
                        children: signatureData
                          ? [
                              new ImageRun({
                                data: signatureData,
                                transformation: {
                                  width: 150,
                                  height: 50,
                                },
                                type: "png",
                              }),
                            ]
                          : [],
                      }),
                    ],
                  }),
                ],
              }),
            ],
          }),
          new Paragraph({
            spacing: { before: 50 },
            indent: { left: convertInchesToTwip(0.4) },
            children: [
              new TextRun({
                text: "Whitney Ingram, CUWCD General Manager",
                size: 22,
              }),
            ],
          }),
        ],
      },
      // === PAGE 2: PERMIT CONDITIONS (separate section with narrower margins) ===
      {
        properties: {
          page: {
            margin: {
              top: convertInchesToTwip(0.6),
              bottom: convertInchesToTwip(0.6),
              left: convertInchesToTwip(0.5),
              right: convertInchesToTwip(0.5),
            },
            borders: {
              pageBorderTop: {
                style: BorderStyle.SINGLE,
                size: 12,
                color: "000000",
                space: 10,
              },
              pageBorderBottom: {
                style: BorderStyle.SINGLE,
                size: 12,
                color: "000000",
                space: 10,
              },
              pageBorderLeft: {
                style: BorderStyle.SINGLE,
                size: 12,
                color: "000000",
                space: 10,
              },
              pageBorderRight: {
                style: BorderStyle.SINGLE,
                size: 12,
                color: "000000",
                space: 10,
              },
              pageBorders: {
                offsetFrom: "page",
              },
            },
          },
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    text: `Permit No: ${permit.permit_number || "—"}`,
                    size: 18,
                    color: "666666",
                  }),
                ],
              }),
            ],
          }),
        },
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { before: 100, after: 200 },
            children: [
              new TextRun({
                text: "Permit conditions and requirements",
                bold: true,
                size: 32,
              }),
            ],
          }),
          new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 300 },
            children: [
              new TextRun({
                text: conditionsSubtitle,
                italics: true,
                size: 22,
              }),
            ],
          }),

          // Intro paragraph
          new Paragraph({
            alignment: AlignmentType.JUSTIFIED,
            spacing: { after: 200 },
            children: [
              new TextRun({ text: PERMIT_CONDITIONS_INTRO, size: 18 }),
            ],
          }),

          // Conditions
          ...permitConditions.map(
            (condition) =>
              new Paragraph({
                alignment: AlignmentType.JUSTIFIED,
                spacing: { after: 200 },
                children: [new TextRun({ text: condition, size: 18 })],
              })
          ),
        ],
      },
    ],
  });
}

/**
 * Generate and download single permit DOCX
 * @param {Object} permit - The permit data
 * @param {Date} issuedDate - The date to use for the issuance line
 */
export async function downloadPermitDocx(permit, issuedDate = new Date()) {
  // Fetch logo and signature data (will use cache if available)
  const [logoData, signatureData] = await Promise.all([
    fetchLogoData(),
    fetchSignatureData(),
  ]);
  const doc = createPermitDocument(permit, {
    logoData,
    signatureData,
    issuedDate,
  });
  const blob = await Packer.toBlob(doc);
  const filename = `Permit-${permit.permit_number || permit.permit_ndx}-${
    permit.permit_year || "unknown"
  }.docx`;
  saveAs(blob, filename);
}

/**
 * Download bulk permits as ZIP from backend (DOCX format)
 */
export async function downloadBulkPermitsZip(
  permitNdxList,
  token,
  issuedDate = new Date()
) {
  const response = await axios.post(
    `${API_ENDPOINT}/api/permit-export/bulk-download`,
    { permit_ndx_list: permitNdxList, issued_date: issuedDate.toISOString() },
    {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    }
  );

  const blob = new Blob([response.data], { type: "application/zip" });
  const filename = `permits-docx-${new Date().toISOString().split("T")[0]}.zip`;
  saveAs(blob, filename);
}

/**
 * Download single permit as PDF from backend
 */
export async function downloadPermitPdf(
  permitNdx,
  permitNumber,
  permitYear,
  token,
  issuedDate = new Date()
) {
  const response = await axios.get(
    `${API_ENDPOINT}/api/permit-export/${permitNdx}/pdf`,
    {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
      params: { issued_date: issuedDate.toISOString() },
    }
  );

  const blob = new Blob([response.data], { type: "application/pdf" });
  const filename = `Permit-${permitNumber || permitNdx}-${
    permitYear || "unknown"
  }.pdf`;
  saveAs(blob, filename);
}

/**
 * Download bulk permits as ZIP from backend (PDF format)
 */
export async function downloadBulkPermitsPdfZip(
  permitNdxList,
  token,
  issuedDate = new Date()
) {
  const response = await axios.post(
    `${API_ENDPOINT}/api/permit-export/bulk-download-pdf`,
    { permit_ndx_list: permitNdxList, issued_date: issuedDate.toISOString() },
    {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    }
  );

  const blob = new Blob([response.data], { type: "application/zip" });
  const filename = `permits-pdf-${new Date().toISOString().split("T")[0]}.zip`;
  saveAs(blob, filename);
}
